import { motion } from "framer-motion";

const ATTORNEYS = ["Tricia Lindsay", "Graham Brownstein", "Barry Silberman"];

export default function SubstackCTA() {
    return (
        <section
            data-testid="substack-section"
            id="follow"
            className="relative w-full py-24 sm:py-32 bg-[color:var(--paper)] border-t border-neutral-300"
        >
            <div className="max-w-[92rem] mx-auto px-6 sm:px-12 lg:px-16">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-15%" }}
                    transition={{ duration: 1 }}
                    className="grid grid-cols-12 gap-6 items-end"
                >
                    <div className="col-span-12 sm:col-span-3 font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-500">
                        Chapter VII — The Case
                    </div>
                    <div className="col-span-12 sm:col-span-9">
                        <h2 className="font-serif text-5xl sm:text-7xl lg:text-8xl leading-[0.9] tracking-tight text-neutral-950">
                            Follow the case.
                        </h2>
                        <p className="mt-6 max-w-xl font-serif italic text-lg sm:text-xl text-neutral-700">
                            Active lawsuit, Nassau County Supreme Court, NY.
                            Rebecca — her mother, her legal guardian —
                            continues to write the record in the open.
                        </p>
                    </div>
                </motion.div>

                {/* Substack CTA — the big card */}
                <motion.a
                    href="https://rebeccacharles.substack.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="substack-link"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1, delay: 0.2 }}
                    className="group mt-14 sm:mt-20 flex items-center justify-between border-y border-neutral-900 py-8 sm:py-10 hover:bg-neutral-950 transition-colors duration-700"
                >
                    <span className="font-serif text-3xl sm:text-5xl lg:text-6xl tracking-tight text-neutral-950 group-hover:text-neutral-50 transition-colors duration-700">
                        rebeccacharles.substack.com
                    </span>
                    <span className="flex items-center gap-4 font-sans text-xs uppercase tracking-[0.3em] text-neutral-950 group-hover:text-neutral-50 transition-colors duration-700">
                        <span className="hidden sm:inline">#JusticeForDanielle</span>
                        <svg
                            width="52"
                            height="18"
                            viewBox="0 0 52 18"
                            fill="none"
                            className="transition-transform duration-700 group-hover:translate-x-3"
                            aria-hidden="true"
                        >
                            <path d="M0 9 H50 M42 1 L50 9 L42 17" stroke="currentColor" strokeWidth="1" fill="none" />
                        </svg>
                    </span>
                </motion.a>

                {/* Legal + Donate + Contact tri-column */}
                <div className="mt-20 sm:mt-28 grid grid-cols-12 gap-8 sm:gap-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-10%" }}
                        transition={{ duration: 1 }}
                        className="col-span-12 sm:col-span-4"
                        data-testid="legal-column"
                    >
                        <div className="font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-500 mb-4">
                            The Legal Team
                        </div>
                        <div className="font-serif text-2xl sm:text-3xl leading-snug text-neutral-950 space-y-1">
                            {ATTORNEYS.map((a) => (
                                <div key={a}>{a}</div>
                            ))}
                        </div>
                        <div className="mt-6 border-t border-neutral-300 pt-4 space-y-1 font-sans text-xs uppercase tracking-[0.28em] text-neutral-600">
                            <div>Nassau County Supreme Court, NY</div>
                            <div className="tabular">Phone · 917-406-5955</div>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-10%" }}
                        transition={{ duration: 1, delay: 0.1 }}
                        className="col-span-12 sm:col-span-4"
                    >
                        <a
                            href="https://freenowfoundation.app.neoncrm.com/forms/28"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="donate-link"
                            className="group block border border-neutral-900 p-8 hover:bg-neutral-950 transition-colors duration-700"
                        >
                            <div className="font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-500 group-hover:text-neutral-400 mb-4 transition-colors duration-700">
                                Support the fight
                            </div>
                            <div className="font-serif text-2xl sm:text-3xl leading-snug text-neutral-950 group-hover:text-neutral-50 transition-colors duration-700">
                                Fund expert witnesses
                                and court fees.
                            </div>
                            <div className="mt-8 flex items-center justify-between font-sans text-xs uppercase tracking-[0.28em] text-neutral-950 group-hover:text-neutral-50 transition-colors duration-700">
                                <span>Free Now Foundation · 501(c)(3)</span>
                                <svg width="30" height="12" viewBox="0 0 30 12" fill="none" aria-hidden="true">
                                    <path d="M0 6 H28 M22 1 L28 6 L22 11" stroke="currentColor" strokeWidth="1" fill="none" />
                                </svg>
                            </div>
                        </a>

                        {/* Danielle painting — sits between the two donation cards */}
                        <figure
                            data-testid="donation-painting"
                            className="mt-4"
                        >
                            <div className="relative w-full overflow-hidden border border-neutral-900 bg-neutral-100">
                                <img
                                    src="/danielle/danielle_painting.webp"
                                    alt="Watercolor portrait of Danielle."
                                    loading="lazy"
                                    className="w-full h-auto block"
                                />
                            </div>
                            <figcaption className="mt-3 font-sans text-[10px] uppercase tracking-[0.3em] text-neutral-500 text-center">
                                For Danielle. Both funds go to the fight.
                            </figcaption>
                        </figure>

                        {/* GiveSendGo — equal-size donate card */}
                        <a
                            href="https://www.givesendgo.com/justicefordanielle?utm_source=share"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="givesendgo-link"
                            aria-label="Support Justice for Danielle on GiveSendGo"
                            className="group mt-4 block border border-neutral-900 p-8 hover:bg-neutral-950 transition-colors duration-700"
                        >
                            <div className="font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-500 group-hover:text-neutral-400 mb-4 transition-colors duration-700">
                                Faith-based support
                            </div>
                            <div className="font-serif text-2xl sm:text-3xl leading-snug text-neutral-950 group-hover:text-neutral-50 transition-colors duration-700">
                                Stand with Danielle.
                                Fuel the fight
                                for justice.
                            </div>
                            <div className="mt-8 flex items-center justify-between font-sans text-xs uppercase tracking-[0.28em] text-neutral-950 group-hover:text-neutral-50 transition-colors duration-700">
                                <span>GiveSendGo · Justice for Danielle</span>
                                <svg width="30" height="12" viewBox="0 0 30 12" fill="none" aria-hidden="true">
                                    <path d="M0 6 H28 M22 1 L28 6 L22 11" stroke="currentColor" strokeWidth="1" fill="none" />
                                </svg>
                            </div>
                        </a>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-10%" }}
                        transition={{ duration: 1, delay: 0.2 }}
                        className="col-span-12 sm:col-span-4"
                        data-testid="statement-column"
                    >
                        <div className="font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-500 mb-4">
                            The promise
                        </div>
                        <p className="font-serif italic text-2xl sm:text-3xl leading-snug text-neutral-950">
                            &ldquo;We will never stop fighting.
                            We will never stop loving.
                            We will never forget.&rdquo;
                        </p>
                        <div className="mt-6 font-sans text-xs uppercase tracking-[0.28em] text-neutral-600">
                            — Rebecca Charles Jackson
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
